import { useEffect, useState } from 'react';
import BackendService from 'BackendService';
import {
  AppPostFormValues,
  EMPTY_APP_POST_FORM,
  NewsItemAdmin,
  appPostToForm,
  isArticleEmpty,
} from '../../../models/NewsItem';

type ToastType = 'success' | 'info' | 'warning' | 'error';

async function uploadAppPostImage(file: File): Promise<string> {
  const backend = new BackendService();
  const ext = file.name.split('.').pop() || 'jpg';
  const baseName = file.name.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9-_]/g, '-');
  const path = `news/${Date.now()}-${baseName}`;
  const { uploadUrl, relativePath } = await backend.generateArbitraryImageUploadUrl(path, ext);
  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!putRes.ok) throw new Error(`Upload failed: ${putRes.status}`);
  return `/api/arbitraryImages/${relativePath}`;
}

export function useAppPostForm({
  open,
  editing,
  onSaveDraft,
  onPublish,
  onShowToast,
}: {
  open: boolean;
  editing: NewsItemAdmin | null;
  onSaveDraft: (values: AppPostFormValues) => Promise<void>;
  onPublish: (values: AppPostFormValues) => Promise<void>;
  onShowToast: (message: string, type: ToastType) => void;
}) {
  const [values, setValues] = useState<AppPostFormValues>(EMPTY_APP_POST_FORM);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  useEffect(() => {
    if (!open) return;
    setValues(editing ? appPostToForm(editing) : EMPTY_APP_POST_FORM);
  }, [open, editing]);
  const canSaveDraft = values.title.trim() !== '';
  const canPublish = canSaveDraft && !isArticleEmpty(values.article) && values.publishDate !== '';
  const isDraftOrNew = !editing || editing.status === 'draft';
  const saveDraft = async () => {
    if (!canSaveDraft) return;
    setSaving(true);
    try {
      await onSaveDraft(values);
    } finally {
      setSaving(false);
    }
  };
  const publish = async () => {
    if (!canPublish) return;
    setPublishing(true);
    try {
      await onPublish(values);
    } finally {
      setPublishing(false);
    }
  };
  const handleImageFile = async (file: File) => {
    setUploadingImage(true);
    try {
      const imageUrl = await uploadAppPostImage(file);
      setValues((v) => ({ ...v, imageUrl }));
    } catch (e) {
      onShowToast(e instanceof Error ? e.message : 'Image upload failed', 'error');
    } finally {
      setUploadingImage(false);
    }
  };
  return {
    values,
    setValues,
    saving,
    publishing,
    uploadingImage,
    canSaveDraft,
    canPublish,
    isDraftOrNew,
    saveDraft,
    publish,
    handleImageFile,
  };
}
